import {
  type ChangeEvent,
  type ReactNode,
  forwardRef,
  useRef,
  useState,
  type ReactElement,
  Children,
} from 'react';
import { useIntl } from 'react-intl';

import { ViewIcon, ViewOffIcon } from '@dynatrace/strato-icons';

import {
  PasswordInputTooltip,
  type PasswordInputTooltipProps,
} from './PasswordInputTooltip.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import { BaseInput } from '../_base-input/BaseInput.js';
import { getPlaceholderMessage } from '../_base-input/baseInputMessages.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
const COMPONENT_NAME = 'PasswordInput';

//#region Typings
/**
 * The properties of the PasswordInput component.
 * @public
 */
export type PasswordInputProps = Pick<
  BaseInputProps,
  | 'placeholder'
  | 'autoComplete'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'onFocus'
  | 'onBlur'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'className'
  | 'style'
  | 'data-testid'
  | 'data-dtrum-mask'
  | 'data-dtrum-allow'
> &
  FormControlProps<
    string,
    (value: string, event?: ChangeEvent<HTMLInputElement>) => void,
    HTMLInputElement
  > &
  WithChildren &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
  };

type PasswordInputSlots = {
  tooltip: ReactElement<PasswordInputTooltipProps> | null;
};
//#endregion Typings

function getTooltipSlot(children: ReactNode) {
  const slots: PasswordInputSlots = {
    tooltip: null,
  };

  for (const child of Children.toArray(children)) {
    if (isSlot(child, PasswordInputTooltip)) {
      slots.tooltip = child;
    } else if (child !== null && isDev()) {
      // eslint-disable-next-line no-console
      console.warn('The used child is not supported by the PasswordInput.');
    }
  }

  return slots;
}

const PasswordInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  PasswordInputProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage(
    getPlaceholderMessage('password'),
  );

  const {
    onChange,
    id: propId,
    name,
    _form,
    _messageTarget,
    readOnly = false,
    disabled,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
    controlState,
    children,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    className: consumerClassName,
    style: consumerStyle,
    defaultValue,
    value,
    placeholder = defaultPlaceholder,
    onValidityChange,
    ...remainingProps
  }: PasswordInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const [hidden, setHidden] = useState(true);

  const [internalValue, setInternalValue] = useControllableState({
    defaultValue,
    value,
  });

  const controlRef = useRef<FormControlRef<HTMLDivElement>>(null);
  const mergedRef = useMergeRefs<FormControlRef<HTMLDivElement> | null>([
    forwardedRef,
    controlRef,
  ]);

  const formControl = useInternalFormRegistration({
    id: propId,
    name,
    form: _form,
    messageTarget: _messageTarget,
    value: internalValue ?? '',
    controlRef,
    onValidityChange,
  });

  const onInternalChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value, event);
    setInternalValue(event.target.value);
  };

  const { tooltip } = getTooltipSlot(children);
  const { showText, hideText, ...remainingTooltipProps } = tooltip?.props ?? {};
  const tooltipBehavioralTrackingProps = extractBehaviorTrackingProps(
    remainingTooltipProps,
  );

  const showTooltipDefaultText = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'tgL6QEjmq+ArASFk',
    description:
      'Text in a tooltip shown when hovering the password input icon to show the password',
  });
  const hideTooltipDefaultText = intl.formatMessage({
    defaultMessage: 'Hide password',
    id: 'vMt3ZRHwBods5xzM',
    description:
      'Text in a tooltip shown when hovering the password input icon to hide the password',
  });
  const dataDtProperties = `value:${hidden ? 'show' : 'hide'}`;

  const showTooltipText = showText ?? showTooltipDefaultText;
  const hideTooltipText = hideText ?? hideTooltipDefaultText;

  return (
    <BaseInput
      {...mergeProps(restProps, behavioralTrackingProps)}
      id={formControl.id}
      value={internalValue}
      role="textbox"
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
      controlState={controlState}
      name={name}
      form={_form}
      type={hidden ? 'password' : 'text'}
      onChange={onInternalChange}
      onValidityChange={formControl.onValidityChange}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={mergedRef}
      className={consumerClassName}
      style={consumerStyle}
      readOnly={readOnly}
      disabled={disabled}
      aria-disabled={ariaDisabledProp}
      data-dt-component={COMPONENT_NAME}
      placeholder={placeholder}
    >
      <BaseInput.Suffix>
        <BaseInput.Button
          onClick={() => setHidden(!hidden)}
          tooltipText={hidden ? showTooltipText : hideTooltipText}
          tooltipBehavioralTrackingProps={tooltipBehavioralTrackingProps}
          aria-label={hidden ? showTooltipText : hideTooltipText}
          id={remainingTooltipProps.id}
          aria-disabled={ariaDisabledProp}
          data-dt-children-name="ShowHidePassword"
          data-dt-properties={dataDtProperties}
          disabled={readOnly ? false : disabled}
        >
          {hidden ? <ViewIcon /> : <ViewOffIcon />}
        </BaseInput.Button>
      </BaseInput.Suffix>
    </BaseInput>
  );
});

/**
 * The `PasswordInput` component can be used to hide user input.
 * Always use a `PasswordInput` when your app asks for sensitive data,
 * such as a password. Users can click on the eye icon to hide or show their data.
 * @public
 */
export const PasswordInput = Object.assign(PasswordInputRoot, {
  Tooltip: PasswordInputTooltip,
});

(PasswordInput as typeof PasswordInput & { displayName: string }).displayName =
  COMPONENT_NAME;
